import React, {useEffect, useState} from "react";

import {Button} from "primereact/button";
import {Dialog} from "primereact/dialog";
import {Tag} from "primereact/tag";
import {Message} from "primereact/message";

import {Loading} from "../../utils/InlineComponents";
import {AppContext} from "../../HomePage";
import {DynamicPricingTemplateService} from "../../service/DynamicPricingTemplateService";
import TemplateForm from "./TemplateForm";
import RuleCard from "./RuleCard";

const service = new DynamicPricingTemplateService();

// Full-page manager for the user's repricing rules. Rules are shared across all of
// the user's campaigns, so this page is mounted at user level (not per campaign).
const RepricingRulesPage = () => {
    const {t, toast, setError} = React.useContext(AppContext);

    const [rules, setRules] = useState([]);
    const [loading, setLoading] = useState(false);

    const [showForm, setShowForm] = useState(false);
    const [draft, setDraft] = useState(null);
    const [editingId, setEditingId] = useState(null);
    const [saving, setSaving] = useState(false);
    const [applyConfirm, setApplyConfirm] = useState(false);   // confirm save & apply scope

    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const [linkedTarget, setLinkedTarget] = useState(null);
    const [linked, setLinked] = useState(null);
    const [linkedLoading, setLinkedLoading] = useState(false);

    const [busyId, setBusyId] = useState(null);            // active/passive toggle in flight
    const [toggleTarget, setToggleTarget] = useState(null); // rule pending toggle confirm

    const reportError = (e) => { if (setError) { setError(e); setError(null); } };
    const ok = (summary, detail) => toast && toast.current &&
        toast.current.show({severity: 'success', summary, detail: detail || t('message_detail.successful')});
    const warn = (summary, detail) => toast && toast.current &&
        toast.current.show({severity: 'warn', summary, detail});

    const load = () => {
        setLoading(true);
        service.list().then(data => setRules(data || [])).catch(reportError).finally(() => setLoading(false));
    };
    useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const openCreate = () => {
        setEditingId(null);
        setDraft({
            name: "", reduction_type: 'amount', reduction_direction: 1, reduction_value: 0,
            protection_strategy: 'min_value', is_active: true, is_auto_update: false,
            no_competitor_strategy: false, ignore_some_competitors: false,
        });
        setShowForm(true);
    };
    const openEdit = (rule) => { setEditingId(rule.id); setDraft({...rule}); setShowForm(true); };

    // Persist the rule. Editing auto-applies the change to its linked products
    // (update then reapply) so the shared rule and its products never drift.
    const persist = () => {
        setSaving(true);
        const isEdit = !!editingId;
        const req = isEdit
            ? service.update(editingId, draft).then(() => service.reapply(editingId))
            : service.create(draft);
        req.then(() => {
            ok(isEdit ? t('pricing_template.edit') : t('pricing_template.new'));
            setShowForm(false); setDraft(null); setEditingId(null); setApplyConfirm(false); load();
        }).catch(reportError).finally(() => setSaving(false));
    };

    const saveForm = () => {
        const name = (draft && draft.name ? draft.name : "").trim();
        if (!name) { warn(t('pricing_template.title'), t('pricing_template.name_required')); return; }
        // Editing a rule that has linked products: confirm the apply scope first,
        // since saving re-applies the change to every linked product.
        if (editingId && (draft.linked_product_count || 0) > 0) { setApplyConfirm(true); return; }
        persist();
    };

    const confirmDelete = () => {
        if (!deleteTarget) return;
        setDeleting(true);
        service.remove(deleteTarget.id, true).then(() => {
            ok(t('pricing_template.delete')); setDeleteTarget(null); load();
        }).catch(reportError).finally(() => setDeleting(false));
    };

    const openLinked = (rule) => {
        setLinkedTarget(rule); setLinked(null); setLinkedLoading(true);
        service.linked(rule.id).then(d => setLinked(d || {total: 0, campaigns: []}))
            .catch(reportError).finally(() => setLinkedLoading(false));
    };

    // Toggle: confirm (because it changes live pricing on linked products), then
    // flip the flag AND re-apply so a deactivation truly stops repricing.
    const confirmToggle = () => {
        const rule = toggleTarget;
        if (!rule) return;
        const next = !rule.is_active;
        setBusyId(rule.id); setToggleTarget(null);
        service.setActive(rule.id, next, rule).then(() => {
            ok(next ? t('pricing_template.toggle_active') : t('pricing_template.toggle_passive'));
            load();
        }).catch(reportError).finally(() => setBusyId(null));
    };

    return (
        <div className="p-grid">
            <div className="p-col-12">
                <div style={{display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, marginBottom: 12}}>
                    <div>
                        <h2 style={{margin: 0, display: 'flex', alignItems: 'center', gap: 10}}>
                            <i className="pi pi-sliders-h"/> {t('menu_item.repricing_rules')}
                        </h2>
                        <p style={{color: '#6b7280', marginTop: 4, marginBottom: 0}}>{t('pricing_template.page_desc')}</p>
                    </div>
                    <Button label={t('pricing_template.new')} icon="pi pi-plus" onClick={openCreate}/>
                </div>

                <Message severity="info" text={t('pricing_template.shared_across_campaigns')}
                         style={{display: 'block', marginBottom: 16}}/>

                {loading
                    ? <div style={{textAlign: 'center'}}><Loading/></div>
                    : rules.length === 0
                        ? (
                            <div style={{textAlign: 'center', padding: '48px 16px', border: '1px dashed #d1d5db',
                                borderRadius: 12, color: '#6b7280'}}>
                                <i className="pi pi-sliders-h" style={{fontSize: 36, color: '#9ca3af'}}/>
                                <h3 style={{margin: '12px 0 4px'}}>{t('pricing_template.empty_title')}</h3>
                                <p style={{marginTop: 0}}>{t('pricing_template.empty_desc')}</p>
                                <Button label={t('pricing_template.new')} icon="pi pi-plus" onClick={openCreate}/>
                            </div>
                        )
                        : (
                            <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(290px, 1fr))', gap: 14}}>
                                {rules.map(rule => (
                                    <RuleCard key={rule.id} rule={rule} t={t} busy={busyId === rule.id}
                                              onToggle={(r) => setToggleTarget(r)} onLinked={openLinked}
                                              onEdit={openEdit} onDelete={(r) => setDeleteTarget(r)}/>
                                ))}
                                <button onClick={openCreate} style={{background: 'transparent', border: '1px dashed #d1d5db',
                                    borderRadius: 10, padding: 16, minHeight: 150, cursor: 'pointer', color: '#6b7280',
                                    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8}}>
                                    <i className="pi pi-plus" style={{fontSize: 22}}/>
                                    <span>{t('pricing_template.new')}</span>
                                </button>
                            </div>
                        )}
            </div>

            <Dialog header={editingId ? t('pricing_template.edit') : t('pricing_template.new')}
                    visible={showForm} style={{width: '640px', maxWidth: '95vw'}} modal
                    onHide={() => !saving && setShowForm(false)}
                    footer={<div>
                        <Button label={t('actions.cancel')} className="p-button-text" disabled={saving} onClick={() => setShowForm(false)}/>
                        <Button label={editingId && ((draft && draft.linked_product_count) || 0) > 0
                                    ? t('pricing_template.save_apply') : t('actions.save')}
                                icon="pi pi-save" loading={saving} disabled={saving} onClick={saveForm}/>
                    </div>}>
                {showForm && <TemplateForm value={draft} onChange={setDraft} t={t}/>}
            </Dialog>

            {/* Save & apply scope confirmation (edit of a rule with linked products) */}
            <Dialog header={t('pricing_template.save_apply')} visible={applyConfirm}
                    style={{width: '460px', maxWidth: '95vw'}} modal
                    onHide={() => !saving && setApplyConfirm(false)}
                    footer={<div>
                        <Button label={t('actions.cancel')} className="p-button-text" disabled={saving} onClick={() => setApplyConfirm(false)}/>
                        <Button label={t('pricing_template.save_apply')} icon="pi pi-check"
                                loading={saving} disabled={saving} onClick={persist}/>
                    </div>}>
                <span>{t('pricing_template.apply_confirm', {
                    products: (draft && draft.linked_product_count) || 0,
                    campaigns: (draft && draft.linked_campaign_count) || 0})}</span>
            </Dialog>

            <Dialog header={t('pricing_template.delete')} visible={!!deleteTarget}
                    style={{width: '480px', maxWidth: '95vw'}} modal
                    onHide={() => !deleting && setDeleteTarget(null)}
                    footer={<div>
                        <Button label={t('actions.cancel')} className="p-button-text" disabled={deleting} onClick={() => setDeleteTarget(null)}/>
                        <Button label={t('pricing_template.delete')} icon="pi pi-trash" className="p-button-danger"
                                loading={deleting} disabled={deleting} onClick={confirmDelete}/>
                    </div>}>
                <p style={{marginTop: 0}}>{t('pricing_template.delete_confirm', {name: deleteTarget ? deleteTarget.name : ''})}</p>
                <Message severity="info" style={{display: 'block', marginTop: 10}}
                         text={t('pricing_template.delete_remove_hint')}/>
            </Dialog>

            <Dialog header={t('pricing_template.linked_products')} visible={!!linkedTarget}
                    style={{width: '640px', maxWidth: '95vw'}} modal onHide={() => setLinkedTarget(null)}
                    footer={<Button label={t('actions.close')} className="p-button-text" onClick={() => setLinkedTarget(null)}/>}>
                {linkedLoading
                    ? <div style={{textAlign: 'center'}}><Loading/></div>
                    : !linked || !linked.total
                        ? <Message severity="info" style={{display: 'block'}} text={t('pricing_template.no_linked_products')}/>
                        : <div style={{display: 'flex', flexDirection: 'column', gap: 10}}>
                            <small style={{color: '#6b7280'}}>{t('pricing_template.linked_total', {count: linked.total})}</small>
                            {(linked.campaigns || []).map(group => (
                                <div key={group.campaign_id || 'none'} style={{display: 'flex', alignItems: 'center',
                                    justifyContent: 'space-between', gap: 8, border: '1px solid #e5e7eb',
                                    borderRadius: 8, padding: '10px 12px'}}>
                                    <span style={{fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8}}>
                                        <i className="pi pi-megaphone" style={{color: '#2563eb'}}/>
                                        {group.campaign_name || group.campaign_id || t('pricing_template.no_campaign')}
                                    </span>
                                    <Tag value={t('pricing_template.used_in_products', {count: group.count})}/>
                                </div>
                            ))}
                        </div>}
            </Dialog>

            <Dialog header={toggleTarget && toggleTarget.is_active ? t('pricing_template.toggle_passive') : t('pricing_template.toggle_active')}
                    visible={!!toggleTarget} style={{width: '460px', maxWidth: '95vw'}} modal
                    onHide={() => setToggleTarget(null)}
                    footer={<div>
                        <Button label={t('actions.cancel')} className="p-button-text" onClick={() => setToggleTarget(null)}/>
                        <Button label={t('actions.confirm')} icon="pi pi-check" onClick={confirmToggle}/>
                    </div>}>
                <span>{toggleTarget && (toggleTarget.is_active
                    ? t('pricing_template.deactivate_confirm', {count: toggleTarget.linked_product_count || 0})
                    : t('pricing_template.activate_confirm', {count: toggleTarget.linked_product_count || 0}))}</span>
            </Dialog>
        </div>
    );
};

export default RepricingRulesPage;
